import { useState } from 'react';
import { Hinweis, Karte, Knopf } from '@/components/ui';
import { useInstallation } from '@/lib/installation';

/**
 * Einladung, die App auf dem Gerät zu installieren (SPEC Kap. 13).
 *
 * Installiert startet die App im Vollbild, mit eigenem Symbol und ohne
 * Adressleiste — vor Ort auf dem Tablet ist das der Unterschied zwischen einer
 * Webseite und einem Werkzeug. Der Offline-Betrieb funktioniert auch im
 * Browser-Tab; die Installation macht ihn nur zuverlässig auffindbar.
 */

/** Kompakter Knopf für die Kopfzeile. Zeigt sich nur, wenn er etwas kann. */
export const InstallierenKnopf = () => {
  const { weg, installieren } = useInstallation();
  const [laedt, setLaedt] = useState(false);

  if (weg !== 'knopf') return null;

  const starten = async (): Promise<void> => {
    setLaedt(true);
    try {
      await installieren();
    } finally {
      setLaedt(false);
    }
  };

  return (
    <Knopf
      aussehen="sekundaer"
      groesse="klein"
      laedt={laedt}
      symbol={<span aria-hidden="true">⤓</span>}
      onClick={() => void starten()}
      title="Übergabeprotokoll auf diesem Gerät installieren"
    >
      <span className="hidden sm:inline">Installieren</span>
      <span className="sr-only sm:hidden">App installieren</span>
    </Knopf>
  );
};

/** Ausführliche Fassung für „Mein Profil" — inklusive Weg über Safari. */
export const InstallationsKarte = () => {
  const { installiert, weg, installieren } = useInstallation();
  const [laedt, setLaedt] = useState(false);
  const [abgelehnt, setAbgelehnt] = useState(false);

  const starten = async (): Promise<void> => {
    setLaedt(true);
    try {
      setAbgelehnt(!(await installieren()));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <Karte
      titel="App auf dem Gerät"
      beschreibung="Installiert läuft das Übergabeprotokoll im Vollbild und startet auch ohne Netz."
    >
      {installiert && (
        <Hinweis art="erfolg" titel="Installiert">
          Diese App läuft bereits als installierte Anwendung. Sie startet vom Startbildschirm auch
          dann, wenn vor Ort kein Netz verfügbar ist.
        </Hinweis>
      )}

      {weg === 'knopf' && (
        <>
          <Knopf groesse="gross" laedt={laedt} onClick={() => void starten()}>
            Jetzt installieren
          </Knopf>
          {abgelehnt && (
            <Hinweis art="info" className="mt-3">
              Die Installation wurde abgebrochen. Sie lässt sich jederzeit über das Menü des
              Browsers nachholen.
            </Hinweis>
          )}
        </>
      )}

      {weg === 'anleitung' && (
        <Hinweis art="info" titel="So installieren Sie die App auf dem iPhone oder iPad">
          <ol className="ml-4 list-decimal space-y-1">
            <li>
              In Safari unten auf <strong>Teilen</strong> tippen (das Quadrat mit dem Pfeil).
            </li>
            <li>
              <strong>Zum Home-Bildschirm</strong> auswählen.
            </li>
            <li>
              Mit <strong>Hinzufügen</strong> bestätigen.
            </li>
          </ol>
          <p className="mt-2">
            Andere Browser auf dem iPhone können das nicht — dieser Weg funktioniert nur in Safari.
          </p>
        </Hinweis>
      )}

      {!installiert && weg === 'keiner' && (
        <Hinweis art="info">
          Dieser Browser bietet keine Installation an. Auf dem Rechner steht sie in Chrome oder Edge
          rechts in der Adressleiste, auf dem iPhone in Safari unter „Teilen → Zum Home-Bildschirm".
          Voraussetzung ist eine Adresse mit <strong>https</strong>.
        </Hinweis>
      )}
    </Karte>
  );
};
